/** Curl the distal palm around the prop without modifying cached source meshes. */
import type { AtelierDraw } from '../../shared/atelier-render';
import type { EquipmentWeaponId } from '../../shared/equipment';
import type { Skeleton,V3 } from './rig';
import { weaponGrip } from './weapons';

export function gripHands(draw:AtelierDraw,sk:Skeleton,weapon:EquipmentWeaponId):AtelierDraw{
  if(draw.material!=='body')return draw;
  const pos=Float32Array.from(draw.pos),nrm=Float32Array.from(draw.nrm),outlineNrm=Float32Array.from(draw.outlineNrm);
  const grips=(weapon==='daggers'?['R','L'] as const:['R'] as const).map(side=>weaponGrip(sk,side,weapon));
  const dot=(a:number[],b:V3)=>a.reduce((sum,v,i)=>sum+v*b[i],0);
  for(const grip of grips){
    const hand=sk.bones[grip.bone],length=Math.hypot(...hand.tail.map((v,i)=>v-hand.head[i]));
    const start=length*.32,radius=length*.27;
    for(let i=0;i<pos.length/3;i++){
      let influence=0;for(let k=0;k<4;k++)if(draw.joints[i*4+k]===grip.bone)influence+=draw.weights[i*4+k];
      if(influence<.2)continue;
      const p=[draw.pos[i*3]-hand.head[0],draw.pos[i*3+1]-hand.head[1],draw.pos[i*3+2]-hand.head[2]];
      const along=dot(p,grip.along);if(along<=start)continue;
      const a=dot(p,grip.across),depth=dot(p,grip.normal),theta=Math.min(Math.PI*1.03,(along-start)/radius);
      const blend=Math.min(1,(influence-.2)/.4),s=Math.sin(theta),c=Math.cos(theta);
      // Fingers wrap toward the shaft, which sits just in front of the palm.
      const bentAlong=start+(radius-depth)*s,bentDepth=radius*(1-c)+depth*c;
      for(let k=0;k<3;k++){
        const bent=hand.head[k]+grip.along[k]*bentAlong+grip.across[k]*a+grip.normal[k]*bentDepth;
        pos[i*3+k]=draw.pos[i*3+k]+(bent-draw.pos[i*3+k])*blend;
      }
      for(const [source,target] of [[draw.nrm,nrm],[draw.outlineNrm,outlineNrm]] as const){
        const n=[source[i*3],source[i*3+1],source[i*3+2]],na=dot(n,grip.along),nb=dot(n,grip.across),nn=dot(n,grip.normal);
        const out=grip.along.map((v,k)=>v*(na*c-nn*s)+grip.across[k]*nb+grip.normal[k]*(na*s+nn*c));
        const len=Math.hypot(...out)||1;for(let k=0;k<3;k++)target[i*3+k]=source[i*3+k]*(1-blend)+out[k]/len*blend;
      }
    }
  }
  return {...draw,pos,nrm,outlineNrm};
}
